import React, { useState, useRef } from 'react';
import { FaImage, FaTimes } from 'react-icons/fa';
import styled from 'styled-components';
import { compressImage, validateImageFile, formatFileSize } from '../../../../../utils/imageCompression';

const ImageUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 16px 0;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e1e5e9;
  background: #f8f9fa;
  animation: fadeIn 0.4s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    animation: imageLoad 0.3s ease-in-out;
  }

  @keyframes imageLoad {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    max-width: 100%;
    height: 160px;
  }
`;

const ImagePlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 300px;
  height: 200px;
  border: 2px dashed #cbd5e0;
  border-radius: 8px;
  background: #f8f9fa;
  color: #718096;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #a0aec0;
    background: #f1f3f4;
  }

  span {
    font-size: 14px;
    text-align: center;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    height: 160px;
    
    span {
      font-size: 12px;
    }
  }
`;

const ImageActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-start;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const UploadButton = styled.button<{ $variant: 'browse' | 'camera' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  background: ${props => props.$variant === 'camera' ? '#4299e1' : '#ffffff'};
  color: ${props => props.$variant === 'camera' ? '#ffffff' : '#4a5568'};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 36px;

  &:hover:not(:disabled) {
    background: ${props => props.$variant === 'camera' ? '#3182ce' : '#f7fafc'};
    border-color: ${props => props.$variant === 'camera' ? '#3182ce' : '#a0aec0'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    font-size: 14px;
  }

  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 14px;
    min-height: 44px;
    
    svg {
      font-size: 16px;
    }
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover:not(:disabled) {
    background: rgba(220, 53, 69, 0.8);
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 12px;
  }

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    
    svg {
      font-size: 14px;
    }
  }
`;

interface ImageUploadProps {
  imageUrl?: string;
  onImageChange: (imageUrl: string | null) => void;
  disabled?: boolean;
}

// Debug du composant ImageUpload
const debugImageUpload = (imageUrl?: string) => {
  console.log('🖼️ DEBUG ImageUpload render:', {
    hasImageUrl: !!imageUrl,
    imageUrlType: typeof imageUrl,
    imageUrlLength: imageUrl?.length || 0,
    imageUrlPrefix: imageUrl?.substring(0, 50) || 'undefined',
    timestamp: new Date().toISOString()
  });
};

export const ImageUpload: React.FC<ImageUploadProps> = ({
  imageUrl,
  onImageChange,
  disabled = false
}) => {
  // Debug à chaque render
  debugImageUpload(imageUrl);
  
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (file: File) => {
    if (!file) return;

    // Valider le fichier
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      alert(validation.error);
      return;
    }

    setIsUploading(true);

    try {
      console.log(`📸 Image originale: ${formatFileSize(file.size)}`);
      
      // Compresser automatiquement l'image
      const compressedBase64 = await compressImage(file, {
        maxWidth: 1200,
        maxHeight: 800,
        quality: 0.8,
        maxSizeKB: 500 // 500KB max
      });

      // Calculer la taille après compression
      const compressedSizeKB = Math.round((compressedBase64.length * 3) / 4 / 1024);
      console.log(`📸 Image compressée: ${compressedSizeKB}KB`);

      onImageChange(compressedBase64);
      setIsUploading(false);
    } catch (error) {
      console.error('Erreur compression image:', error);
      alert('Erreur lors du traitement de l\'image. Veuillez réessayer avec une image plus petite.');
      setIsUploading(false);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleCameraClick = () => {
    cameraInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    onImageChange(null);
  };

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  return (
    <ImageUploadContainer>
      {imageUrl ? (
        <ImagePreview>
          <img src={imageUrl} alt="Aperçu du bien" />
          <RemoveButton
            type="button"
            onClick={handleRemoveImage}
            disabled={disabled}
            title="Supprimer l'image"
          >
            <FaTimes />
          </RemoveButton>
        </ImagePreview>
      ) : (
        <ImagePlaceholder>
          <FaImage size={24} />
          <span>Ajouter une photo du bien</span>
        </ImagePlaceholder>
      )}

      <ImageActions>
        {/* Input caché pour parcourir fichiers */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }}
          style={{ display: 'none' }}
        />

        {/* Input caché pour caméra (mobile) */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }}
          style={{ display: 'none' }}
        />

        {/* Boutons d'action */}
        <UploadButton
          type="button"
          onClick={handleBrowseClick}
          disabled={disabled || isUploading}
          $variant="browse"
        >
          <FaImage />
          {isMobile ? 'Galerie' : 'Parcourir'}
        </UploadButton>

        {isMobile && (
          <UploadButton
            type="button"
            onClick={handleCameraClick}
            disabled={disabled || isUploading}
            $variant="camera"
          >
            <FaImage />
            Caméra
          </UploadButton>
        )}
      </ImageActions>

      {isUploading && (
        <div style={{ 
          textAlign: 'center', 
          fontSize: '12px', 
          color: '#666',
          marginTop: '8px'
        }}>
          📸 Compression et optimisation...
        </div>
      )}
    </ImageUploadContainer>
  );
};

export default ImageUpload;

